import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useGetContractQuery } from '@/apis/extended/contractApi';
import { useGetContractProposalsQuery } from '@/apis/extended/proposalApi';
import { VoidContractButton } from '@/components/Negotiation/VoidContractButton';
import { passedAcceptanceDeadline } from '@/utils/acceptanceDeadline';
import { formatContractMoney } from '@/utils/contractFormat';
import { parseDateOnly } from '@/utils/dateOnly';
import { contractAddressLine } from '@/utils/productAddress';
import { Product } from '@/types/ProductType';

type ProductContractSummaryProps = {
  product?: Product;
  contractId?: string;
  userRole?: 'agent' | 'seller' | 'buyer';
  contractStatus?: string;
  isRatified?: boolean;
  showRejectButton?: boolean;
  onRejectSuccess?: () => void;
};

export const ProductContractSummary = ({
  product,
  contractId,
  userRole = 'agent',
  contractStatus: propContractStatus,
  isRatified: propIsRatified,
  showRejectButton = false,
  onRejectSuccess,
}: ProductContractSummaryProps) => {
  const { data: contract } = useGetContractQuery(contractId ?? '', {
    skip: !contractId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });

  const theme = useTheme();

  // An open price counter (the agent's or either party's), so the card
  // doesn't keep showing only the original offer.
  const { data: pendingData } = useGetContractProposalsQuery(
    { contractId: contractId ?? '', status: 'pending' },
    { skip: !contractId }
  );
  const priceCounter = (pendingData?.proposals ?? []).find(
    (p) => p.clauseId === 'purchase-price'
  );

  // Use prop values or fall back to contract values
  const contractStatus = propContractStatus || contract?.contractStatus;
  const isRatified =
    propIsRatified || contract?.isRatified || contractStatus === 'ratified';
  const isVoided = contractStatus === 'voided';

  // Same source as the Deal Room header: the property first.
  const propertyAddress =
    contractAddressLine(contract as any, product as any) || 'N/A';

  // Get offer price
  const offerPrice =
    contract?.offerSpecifics?.offerPrice ||
    contract?.agentOfferInfo?.offerPrice ||
    product?.price ||
    0;

  // Get closing date
  const closingDate =
    contract?.offerSpecifics?.closingDate ||
    contract?.agentOfferInfo?.closingDate ||
    null;

  // Get financing commitment date
  const financingCommitmentDate =
    contract?.offerSpecifics?.financingCommittmentDate || null;

  // Get financing type
  // offerType + financingLoanProgram describe the financing; loanProgram is
  // occupancy (e.g. primary_residence), not cash vs. mortgage.
  const os = contract?.offerSpecifics;
  const financingType =
    os?.offerType === 'Cash'
      ? 'Cash Offer'
      : os?.offerType === 'Financed'
      ? `Mortgage Financing${
          os?.financingLoanProgram
            ? ` (${String(os.financingLoanProgram).toUpperCase()})`
            : ''
        }`
      : 'N/A';

  // Get contract created date as offer date
  const offerDate = contract?.createdAt || null;

  // Derived from the fields the current offer → finalize → sign flow writes
  // (the old agentEmailSent/agentSign/sellerEmailSent flags are never set).
  const checkItems = useMemo(() => {
    const c = contract as any;
    const sentForSignatures =
      isRatified ||
      [
        'awaiting_signatures',
        'awaiting_buyer_signature',
        'awaiting_seller_signature',
      ].includes(String(contractStatus || ''));
    return [
      { label: 'Offer received', value: Boolean(contract) },
      {
        label: 'Negotiations finalized',
        value: Boolean(c?.negotiationsConfirmedAt) || sentForSignatures,
      },
      { label: 'Contract sent for signatures', value: sentForSignatures },
      { label: 'Buyer signed', value: Boolean(c?.buyerSign?.isStored) },
      { label: 'Seller signed', value: Boolean(c?.sellerSign?.isStored) },
      { label: 'Contract ratified', value: Boolean(isRatified) },
    ];
  }, [contract, contractStatus, isRatified]);

  return (
    <Card>
      <CardHeader
        title="Contract Summary"
        avatar={<i className="fa-light fa-clipboard-list-check" />}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Small Reject Button - Only for seller when showRejectButton is true */}
            {showRejectButton && userRole === 'seller' && contractId && (
              <VoidContractButton
                contractId={contractId}
                voidedBy="seller"
                contractStatus={contractStatus}
                isRatified={isRatified}
                onVoidSuccess={onRejectSuccess}
                buttonText="Reject"
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              />
            )}

            {/* Status Badge */}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: '16px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: isRatified
                  ? '#22c55e'
                  : isVoided
                  ? '#dc2626'
                  : contractStatus === 'negotiating' ||
                    contractStatus === 'draft'
                  ? '#f97316'
                  : contractStatus === 'awaiting_signatures'
                  ? '#3b82f6'
                  : '#6b7280',
                color: 'white',
                textTransform: 'capitalize',
              }}
            >
              {isRatified
                ? 'Ratified'
                : isVoided
                ? 'Voided'
                : contractStatus?.replace(/_/g, ' ') || 'Draft'}
            </Box>
          </Box>
        }
      />
      <CardContent>
        {/* Same warning as the Negotiate page, so it's seen from the list. */}
        {userRole === 'agent' &&
          !isRatified &&
          !isVoided &&
          contractId &&
          passedAcceptanceDeadline(contract as any) && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  component={RouterLink}
                  to={`/agent/contract/${contractId}`}
                  color="warning"
                  size="small"
                >
                  Propose new date
                </Button>
              }
            >
              The written-acceptance date has passed. Agree a new date before
              the contract can be finalized or signed.
            </Alert>
          )}
        {/* Property & Contract Details */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.greyShades.grey1 }}
            >
              Property Address
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {propertyAddress}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Offer Amount
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#22c55e' }}
              >
                ${offerPrice?.toLocaleString() || '0'}
              </Typography>
              {priceCounter && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: '#b45309', fontWeight: 600 }}
                >
                  Counter {formatContractMoney(priceCounter.proposedValue)} ·
                  waiting on {priceCounter.responseRequiredFrom}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Offer Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {offerDate ? new Date(offerDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Closing Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {closingDate
                  ? parseDateOnly(closingDate)?.toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Financing Commitment
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {financingCommitmentDate
                  ? parseDateOnly(financingCommitmentDate)?.toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Financing Type
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
              >
                {financingType}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Contract Progress Checklist */}
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Contract Progress
          </Typography>
          {checkItems.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                ml: 0.5,
                mt: 1,
              }}
            >
              {item.value ? (
                <i
                  className="fa-light fa-check color-success"
                  style={{ fontSize: '14px' }}
                />
              ) : (
                <i
                  className="fa-light fa-close color-error"
                  style={{ fontSize: '14px' }}
                />
              )}
              <Typography
                sx={{
                  color: theme.palette.greyShades.grey1,
                  fontSize: '0.875rem',
                }}
                variant="body2"
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
